import { decodeJwtPayload } from './jwt';
import type { AttemptIdempotencyKey } from './operation-attempt';

export interface StoredSession {
  accessToken: string;
  expiresAt: number;
  user: {
    id: string;
    email: string;
    phone: string | null;
    firstName?: string | null;
    lastName?: string | null;
    roles: readonly string[];
    permissions: readonly string[];
    tenantId: string | null;
    tenantName: string | null;
    hasPassword?: boolean;
    clubTimezone?: string | null;
  };
}

export interface ApiProblem {
  status: number;
  code?: string;
  message: string;
  details?: unknown;
  correlationId?: string;
  retryAfterMs?: number;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly details?: unknown;
  readonly correlationId?: string;
  readonly retryAfterMs?: number;

  constructor(problem: ApiProblem) {
    super(problem.message);
    this.name = 'ApiError';
    this.status = problem.status;
    if (problem.code !== undefined) this.code = problem.code;
    if (problem.details !== undefined) this.details = problem.details;
    if (problem.correlationId !== undefined) this.correlationId = problem.correlationId;
    if (problem.retryAfterMs !== undefined) this.retryAfterMs = problem.retryAfterMs;
  }

  isNetwork(): boolean {
    return this.status === 0;
  }
  isTimeout(): boolean {
    return this.code === REQUEST_TIMEOUT_CODE;
  }
  isAuth(): boolean {
    return this.status === 401;
  }
  isForbidden(): boolean {
    return this.status === 403;
  }
  isNotFound(): boolean {
    return this.status === 404;
  }
  isConflict(): boolean {
    return this.status === 409;
  }
  isServer(): boolean {
    return this.status >= 500;
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body' | 'headers'> {
  body?: unknown;
  headers?: Record<string, string>;
  skipAuth?: boolean;
  idempotencyKey?: AttemptIdempotencyKey;
  correlationId?: string;
  query?: Record<string, string | number | boolean | undefined | null>;
  timeoutMs?: number;
}

export const REQUEST_TIMEOUT_MS = 30_000;
export const WRITE_TIMEOUT_MS = 60_000;
export const REFRESH_TIMEOUT_MS = 15_000;
export const DOWNLOAD_TIMEOUT_MS = 300_000;
export const REQUEST_TIMEOUT_CODE = 'request_timeout';

export interface HttpClientConfig {
  storageKey: string;
}

export type SessionListener = (next: StoredSession | null, previous: StoredSession | null) => void;

export const REFRESH_SKEW_MS = 90_000;

export const sessionScopeOf = (session: StoredSession | null): string | null =>
  session ? `${session.user.id}|${session.user.tenantId ?? ''}` : null;

export const refreshRefusalEndsTheSession = (status: number): boolean =>
  status >= 400 && status < 500 && status !== 408 && status !== 429;

type RefreshOutcome =
  | { kind: 'fresh'; token: string }
  | { kind: 'ended' }
  | { kind: 'failed'; error: ApiError }
  | { kind: 'superseded' };

interface RefreshSource {
  token: string;
  scope: string | null;
}

const sessionChanged = (): ApiError =>
  new ApiError({ status: 0, code: 'session_changed', message: 'Сессия сменилась, пока шёл запрос' });

const sessionEnded = (): ApiError =>
  new ApiError({ status: 401, code: 'session_ended', message: 'Сессия закончилась — войдите снова' });

const KEEPER_RETRY_MS = 15_000;

export const readRetryAfter = (raw: string | null, now: number = Date.now()): number | undefined => {
  if (!raw) return undefined;
  const value = raw.trim();
  if (/^\d+$/.test(value)) return Number(value) * 1000;
  const at = Date.parse(value);
  return Number.isNaN(at) ? undefined : Math.max(0, at - now);
};

interface Deadline {
  signal: AbortSignal;
  expired: () => boolean;
  dispose: () => void;
}

const deadlineOf = (caller: AbortSignal | null | undefined, ms: number): Deadline => {
  const controller = new AbortController();
  let expired = false;
  const timer = setTimeout(() => {
    expired = true;
    controller.abort(new DOMException('Request deadline exceeded', 'TimeoutError'));
  }, ms);
  const follow = (): void => controller.abort(caller?.reason);
  if (caller?.aborted) follow();
  else caller?.addEventListener('abort', follow, { once: true });
  return {
    signal: controller.signal,
    expired: () => expired,
    dispose: () => {
      clearTimeout(timer);
      caller?.removeEventListener('abort', follow);
    },
  };
};

const timedOut = (method: string): ApiError =>
  new ApiError({
    status: 0,
    code: REQUEST_TIMEOUT_CODE,
    message:
      method === 'GET'
        ? 'Сервер не ответил вовремя. Проверьте связь и попробуйте ещё раз.'
        : 'Сервер не ответил вовремя. Действие могло выполниться — обновите данные, прежде чем повторять.',
  });

const abortReason = (caller: AbortSignal | null | undefined): unknown =>
  caller?.reason ?? new DOMException('The operation was aborted.', 'AbortError');

const untilAborted = <T>(signal: AbortSignal, pending: Promise<T>): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }
    const onAbort = (): void => reject(signal.reason);
    signal.addEventListener('abort', onAbort, { once: true });
    pending.then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      (err: unknown) => {
        signal.removeEventListener('abort', onAbort);
        reject(err);
      },
    );
  });

const parseProblem = async (response: Response): Promise<ApiProblem> => {
  const correlationId = response.headers.get('x-correlation-id') ?? undefined;
  const retryAfterMs = readRetryAfter(response.headers.get('retry-after'));
  const base: ApiProblem = {
    status: response.status,
    message: response.statusText || 'Request failed',
    ...(correlationId ? { correlationId } : {}),
    ...(retryAfterMs !== undefined ? { retryAfterMs } : {}),
  };
  try {
    const body = (await response.clone().json()) as unknown;
    if (body && typeof body === 'object') {
      const err = (body as { error?: { code?: string; message?: string; details?: unknown } }).error;
      if (err) {
        return {
          ...base,
          ...(err.code !== undefined ? { code: err.code } : {}),
          message: err.message ?? base.message,
          ...(err.details !== undefined ? { details: err.details } : {}),
        };
      }
      if ('title' in body || 'detail' in body || 'type' in body || 'errors' in body) {
        const pd = body as {
          title?: string;
          detail?: string;
          type?: string;
          code?: string;
          traceId?: string;
          errors?: Record<string, string[]>;
          details?: unknown;
        };
        const firstError =
          pd.errors && Object.keys(pd.errors).length > 0
            ? Object.values(pd.errors).flat().find((m) => typeof m === 'string')
            : undefined;
        return {
          ...base,
          ...((pd.code ?? pd.type) !== undefined ? { code: (pd.code ?? pd.type)! } : {}),
          message: pd.detail ?? firstError ?? pd.title ?? base.message,
          ...(pd.details !== undefined
            ? { details: pd.details }
            : pd.errors !== undefined
              ? { details: pd.errors }
              : {}),
          ...(pd.traceId !== undefined ? { correlationId: pd.traceId } : {}),
        };
      }
    }
  } catch {
  }
  return base;
};

const buildUrl = (path: string, query?: RequestOptions['query']): string => {
  const base = path.startsWith('http') ? path : `/api/v1${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    params.append(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${base}${base.includes('?') ? '&' : '?'}${qs}` : base;
};

const buildHeaders = (
  options: RequestOptions,
  token: string | null,
  hasBody: boolean,
): HeadersInit => {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers ?? {}),
  };
  if (token && !options.skipAuth) headers.Authorization = `Bearer ${token}`;
  if (options.idempotencyKey) headers['X-Idempotency-Key'] = options.idempotencyKey;
  if (options.correlationId) headers['X-Correlation-Id'] = options.correlationId;
  return headers;
};

const saveBlobAs = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const withinDeadline = async <T>(
  deadline: Deadline,
  caller: AbortSignal | null | undefined,
  method: string,
  run: () => Promise<T>,
  isTransportFailure: (err: unknown) => boolean,
): Promise<T> => {
  try {
    return await untilAborted(deadline.signal, run());
  } catch (err) {
    if (caller?.aborted) throw abortReason(caller);
    if (deadline.expired()) throw timedOut(method);
    if ((err as { name?: unknown } | null)?.name === 'AbortError') throw err;
    if (!isTransportFailure(err)) throw err;
    throw new ApiError({
      status: 0,
      code: 'network_error',
      message: err instanceof Error && err.message ? err.message : 'Network error',
    });
  }
};

const doFetch = (
  url: string,
  init: RequestInit,
  deadline: Deadline,
  caller: AbortSignal | null | undefined,
): Promise<Response> =>
  withinDeadline(
    deadline,
    caller,
    init.method ?? 'GET',
    () => fetch(url, { ...init, signal: deadline.signal }),
    () => true,
  );

const readBody = <T>(
  deadline: Deadline,
  caller: AbortSignal | null | undefined,
  method: string,
  read: () => Promise<T>,
): Promise<T> =>
  withinDeadline(deadline, caller, method, read, (err) => err instanceof TypeError);

const hasSessionShape = (value: unknown): value is StoredSession => {
  if (typeof value !== 'object' || value === null) return false;
  const s = value as { accessToken?: unknown; expiresAt?: unknown; user?: unknown };
  if (typeof s.accessToken !== 'string' || typeof s.expiresAt !== 'number' || !Number.isFinite(s.expiresAt)) return false;
  if (typeof s.user !== 'object' || s.user === null) return false;
  const u = s.user as { id?: unknown; permissions?: unknown; roles?: unknown };
  return typeof u.id === 'string' && Array.isArray(u.permissions) && Array.isArray(u.roles);
};

export const createHttpClient = (config: HttpClientConfig) => {
  const { storageKey } = config;

  const parseSession = (raw: string | null): StoredSession | null => {
    if (!raw) return null;
    let session: StoredSession | null = null;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (hasSessionShape(parsed)) {
        if (parsed.user.phone === undefined) parsed.user.phone = null;
        session = parsed;
      }
    } catch {
      session = null;
    }
    if (!session && typeof localStorage !== 'undefined') localStorage.removeItem(storageKey);
    return session;
  };

  const readSession = (): StoredSession | null =>
    typeof localStorage === 'undefined' ? null : parseSession(localStorage.getItem(storageKey));

  const listeners = new Set<SessionListener>();
  let published: StoredSession | null = null;

  const publish = (next: StoredSession | null): void => {
    const previous = published;
    published = next;
    for (const listener of [...listeners]) listener(next, previous);
  };

  const writeSession = (session: StoredSession | null): void => {
    if (typeof localStorage === 'undefined') return;
    if (session) {
      localStorage.setItem(storageKey, JSON.stringify(session));
    } else {
      localStorage.removeItem(storageKey);
    }
    publish(session);
  };

  const onOtherTab = (e: StorageEvent): void => {
    if (e.key === storageKey || e.key === null) publish(readSession());
  };

  const subscribe = (listener: SessionListener): (() => void) => {
    if (listeners.size === 0) {
      published = readSession();
      if (typeof window !== 'undefined') window.addEventListener('storage', onOtherTab);
    }
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0 && typeof window !== 'undefined') {
        window.removeEventListener('storage', onOtherTab);
      }
    };
  };

  const claimList = (
    payload: Record<string, unknown>,
    ...names: readonly string[]
  ): string[] => {
    for (const name of names) {
      const raw = payload[name];
      if (Array.isArray(raw)) return raw.filter((v): v is string => typeof v === 'string');
      if (typeof raw === 'string') return [raw];
    }
    return [];
  };

  let inflight: { from: string; promise: Promise<RefreshOutcome> } | null = null;

  const performRefresh = async (session: StoredSession): Promise<RefreshOutcome> => {
    const deadline = deadlineOf(undefined, REFRESH_TIMEOUT_MS);
    try {
      return await refreshWithin(session, deadline);
    } finally {
      deadline.dispose();
    }
  };

  const refreshTimedOut = (): RefreshOutcome => ({
    kind: 'failed',
    error: new ApiError({
      status: 0,
      code: REQUEST_TIMEOUT_CODE,
      message: 'Сервер не продлил сессию вовремя. Проверьте связь и попробуйте ещё раз.',
    }),
  });

  const refreshWithin = async (session: StoredSession, deadline: Deadline): Promise<RefreshOutcome> => {
    const stillCurrent = (): boolean => readSession()?.accessToken === session.accessToken;
    let response: Response;
    try {
      response = await untilAborted(
        deadline.signal,
        fetch('/api/v1/auth/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'include',
          body: '{}',
          signal: deadline.signal,
        }),
      );
    } catch (err) {
      if (!stillCurrent()) return { kind: 'superseded' };
      if (deadline.expired()) return refreshTimedOut();
      return {
        kind: 'failed',
        error: new ApiError({
          status: 0,
          code: 'network_error',
          message: err instanceof Error ? err.message : 'Network error',
        }),
      };
    }

    if (!response.ok) {
      if (!stillCurrent()) return { kind: 'superseded' };
      if (refreshRefusalEndsTheSession(response.status)) {
        writeSession(null);
        return { kind: 'ended' };
      }
      const problem = await untilAborted(deadline.signal, parseProblem(response)).catch(() => null);
      if (!stillCurrent()) return { kind: 'superseded' };
      if (problem === null) return refreshTimedOut();
      return { kind: 'failed', error: new ApiError(problem) };
    }

    let body: {
      accessToken?: string;
      expiresAt?: string | number;
      user?: { tenantId?: string | null; tenantName?: string | null; clubTimezone?: string | null };
    };
    try {
      body = (await untilAborted(deadline.signal, response.json())) as typeof body;
    } catch {
      body = {};
    }
    if (!stillCurrent()) return { kind: 'superseded' };
    if (!body.accessToken && deadline.expired()) return refreshTimedOut();

    if (!body.accessToken) {
      return {
        kind: 'failed',
        error: new ApiError({
          status: response.status,
          code: 'refresh_unreadable',
          message: 'Сервер продлил сессию ответом без токена',
        }),
      };
    }
    const expiresAt =
      typeof body.expiresAt === 'number'
        ? body.expiresAt
        : body.expiresAt
          ? new Date(body.expiresAt).getTime()
          : Date.now() + 15 * 60_000;
    const claims = decodeJwtPayload(body.accessToken) ?? {};
    const next: StoredSession = {
      ...session,
      accessToken: body.accessToken,
      expiresAt,
      user: {
        ...session.user,
        roles: claimList(claims, 'role', 'roles'),
        permissions: claimList(claims, 'permission', 'permissions'),
        ...(body.user
          ? {
              tenantId: body.user.tenantId ?? null,
              tenantName: body.user.tenantName ?? null,
              clubTimezone: body.user.clubTimezone ?? null,
            }
          : {}),
      },
    };
    writeSession(next);
    return { kind: 'fresh', token: body.accessToken };
  };

  const withCrossTabLock = async (run: () => Promise<RefreshOutcome>): Promise<RefreshOutcome> => {
    const locks = (globalThis.navigator as Navigator | undefined)?.locks;
    if (!locks) return run();
    const wait = deadlineOf(undefined, 2 * REFRESH_TIMEOUT_MS);
    try {
      return await locks.request(`${storageKey}:refresh`, { signal: wait.signal }, () => {
        wait.dispose();
        return run();
      });
    } catch (err) {
      if (wait.expired()) return refreshTimedOut();
      throw err;
    } finally {
      wait.dispose();
    }
  };

  const refreshFrom = (from: RefreshSource): Promise<RefreshOutcome> => {
    if (inflight?.from === from.token) return inflight.promise;
    const promise = withCrossTabLock(async () => {
      const current = readSession();
      if (!current || sessionScopeOf(current) !== from.scope) return { kind: 'superseded' };
      if (current.accessToken !== from.token) return { kind: 'fresh', token: current.accessToken };
      return performRefresh(current);
    }).finally(() => {
      if (inflight?.promise === promise) inflight = null;
    });
    inflight = { from: from.token, promise };
    return promise;
  };

  const acquireToken = async (): Promise<RefreshOutcome | null> => {
    const session = readSession();
    if (!session) return null;
    if (inflight?.from === session.accessToken) return inflight.promise;
    if (session.expiresAt > Date.now() + REFRESH_SKEW_MS) {
      return { kind: 'fresh', token: session.accessToken };
    }
    return refreshFrom({ token: session.accessToken, scope: sessionScopeOf(session) });
  };

  const ensureFreshAccessToken = async (): Promise<string | null> => {
    const outcome = await acquireToken();
    return outcome?.kind === 'fresh' ? outcome.token : null;
  };

  const renewAccessToken = async (): Promise<string | null> => {
    const session = readSession();
    if (!session) return null;
    const outcome = await refreshFrom({ token: session.accessToken, scope: sessionScopeOf(session) });
    return outcome.kind === 'fresh' ? outcome.token : null;
  };

  const keepFresh = (): (() => void) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;

    const schedule = (): void => {
      clearTimeout(timer);
      const session = readSession();
      if (stopped || !session) return;
      const due = session.expiresAt - REFRESH_SKEW_MS - Date.now();
      timer = setTimeout(check, due > 0 ? due : KEEPER_RETRY_MS);
    };
    const check = (): void => {
      void acquireToken().finally(schedule);
    };
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') check();
    };

    const unsubscribe = subscribe(schedule);
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
    check();

    return () => {
      stopped = true;
      clearTimeout(timer);
      unsubscribe();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
      }
    };
  };

  const assertAnswersTo = (scope: string | null, options: RequestOptions): void => {
    if (!options.skipAuth && sessionScopeOf(readSession()) !== scope) throw sessionChanged();
  };

  const send = async (
    path: string,
    options: RequestOptions,
    deadline: Deadline,
  ): Promise<{ response: Response; scope: string | null }> => {
    const hasBody = options.body !== undefined;
    const scope = sessionScopeOf(readSession());
    const caller = options.signal;
    const method = options.method ?? 'GET';
    const underDeadline = <T>(pending: () => Promise<T>): Promise<T> =>
      withinDeadline(deadline, caller, method, pending, () => false);

    let token: string | null = null;
    if (!options.skipAuth) {
      const outcome = await underDeadline(acquireToken);
      if (outcome?.kind === 'failed') throw outcome.error;
      if (outcome?.kind === 'ended') throw sessionEnded();
      if (outcome?.kind === 'superseded') throw sessionChanged();
      token = outcome?.token ?? null;
    }

    const url = buildUrl(path, options.query);
    const init: RequestInit = {
      ...options,
      credentials: 'include',
      headers: buildHeaders(options, token, hasBody),
      body: hasBody ? JSON.stringify(options.body) : undefined,
    };

    let response = await doFetch(url, init, deadline, caller);

    if (response.status === 401 && token !== null) {
      const outcome = await underDeadline(() => refreshFrom({ token, scope }));
      if (outcome.kind === 'failed') throw outcome.error;
      if (outcome.kind === 'superseded') throw sessionChanged();
      if (outcome.kind === 'fresh') {
        const retryInit: RequestInit = {
          ...init,
          headers: { ...(init.headers as Record<string, string>), Authorization: `Bearer ${outcome.token}` },
        };
        response = await doFetch(url, retryInit, deadline, caller);
      }
    }

    if (!response.ok) {
      const problem = await readBody(deadline, caller, method, () => parseProblem(response));
      console.error(`[api] ${method} ${url} → ${response.status}`, problem);
      throw new ApiError(problem);
    }

    return { response, scope };
  };

  const deadlineFor = (options: RequestOptions, fallbackMs: number): Deadline =>
    deadlineOf(options.signal, options.timeoutMs ?? fallbackMs);

  const request = async <T = unknown>(path: string, options: RequestOptions = {}): Promise<T> => {
    const method = options.method ?? 'GET';
    const deadline = deadlineFor(options, method === 'GET' ? REQUEST_TIMEOUT_MS : WRITE_TIMEOUT_MS);
    try {
      const { response, scope } = await send(path, options, deadline);

      let body: T;
      const contentType = response.headers.get('content-type') ?? '';
      if (response.status === 204) body = undefined as T;
      else if (contentType.includes('application/json')) {
        body = (await readBody(deadline, options.signal, method, () => response.json())) as T;
      } else {
        body = (await readBody(deadline, options.signal, method, () => response.text())) as unknown as T;
      }
      assertAnswersTo(scope, options);
      return body;
    } finally {
      deadline.dispose();
    }
  };

  const download = async (
    path: string,
    fileName: string,
    options: RequestOptions = {},
  ): Promise<void> => {
    const method = options.method ?? 'GET';
    const deadline = deadlineFor(options, DOWNLOAD_TIMEOUT_MS);
    try {
      const { response, scope } = await send(
        path,
        { ...options, headers: { Accept: '*/*', ...(options.headers ?? {}) } },
        deadline,
      );
      const blob = await readBody(deadline, options.signal, method, () => response.blob());
      assertAnswersTo(scope, options);
      saveBlobAs(blob, fileName);
    } finally {
      deadline.dispose();
    }
  };

  const upload = async <T>(
    path: string,
    form: FormData,
    onProgress?: (fraction: number) => void,
    signal?: AbortSignal,
  ): Promise<T> => {
    const scope = sessionScopeOf(readSession());
    const outcome = await acquireToken();
    if (outcome?.kind === 'failed') throw outcome.error;
    if (outcome?.kind === 'ended') throw sessionEnded();
    if (outcome?.kind === 'superseded') throw sessionChanged();
    const token = outcome?.token ?? null;
    return new Promise<T>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', buildUrl(path));
      xhr.withCredentials = true;
      xhr.timeout = DOWNLOAD_TIMEOUT_MS;
      xhr.setRequestHeader('Accept', 'application/json');
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) onProgress?.(event.loaded / event.total);
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            assertAnswersTo(scope, {});
            resolve(JSON.parse(xhr.responseText) as T);
          } catch (err) {
            reject(err);
          }
          return;
        }
        const headers = new Headers();
        for (const name of ['content-type', 'x-correlation-id', 'retry-after']) {
          const value = xhr.getResponseHeader(name);
          if (value !== null) headers.set(name, value);
        }
        void parseProblem(
          new Response(xhr.responseText || null, { status: xhr.status, statusText: xhr.statusText, headers }),
        ).then((problem) => reject(new ApiError(problem)), reject);
      };
      xhr.onerror = () => reject(new ApiError({ status: 0, code: 'network_error', message: 'Network error' }));
      xhr.ontimeout = () => reject(timedOut('POST'));
      xhr.onabort = () => reject(new DOMException('Выгрузка отменена', 'AbortError'));
      if (signal?.aborted) {
        reject(new DOMException('Выгрузка отменена', 'AbortError'));
        return;
      }
      const onAbort = (): void => xhr.abort();
      signal?.addEventListener('abort', onAbort, { once: true });
      xhr.onloadend = () => signal?.removeEventListener('abort', onAbort);
      xhr.send(form);
    });
  };

  const http = {
    upload: <T = unknown>(
      path: string,
      form: FormData,
      onProgress?: (fraction: number) => void,
      signal?: AbortSignal,
    ) => upload<T>(path, form, onProgress, signal),
    download: (path: string, fileName: string, options: Omit<RequestOptions, 'body'> = {}) =>
      download(path, fileName, options),
    get: <T = unknown>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
      request<T>(path, { ...options, method: 'GET' }),
    post: <T = unknown>(path: string, body?: unknown, options: Omit<RequestOptions, 'method'> = {}) =>
      request<T>(path, { ...options, method: 'POST', body }),
    put: <T = unknown>(path: string, body?: unknown, options: Omit<RequestOptions, 'method'> = {}) =>
      request<T>(path, { ...options, method: 'PUT', body }),
    patch: <T = unknown>(path: string, body?: unknown, options: Omit<RequestOptions, 'method'> = {}) =>
      request<T>(path, { ...options, method: 'PATCH', body }),
    delete: <T = unknown>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
      request<T>(path, { ...options, method: 'DELETE' }),
  };

  return {
    readSession,
    writeSession,
    subscribe,
    keepFresh,
    ensureFreshAccessToken,
    renewAccessToken,
    request,
    download,
    http,
  };
};
